// Comprime una imagen en el navegador antes de subirla, al estilo de lo que
// hace WhatsApp: primero topa la dimensión más larga y después baja la calidad
// del JPEG hasta aterrizar en el peso objetivo.
//
// El back impone su propio tope de 5 MB y valida el formato, así que esto es una
// optimización (subidas más rápidas, menos tráfico), no una validación.

// Lo que acepta el endpoint.
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

// El tope de dimensión hace la mayor parte del trabajo: una foto de 4032px
// bajada a 1600px con calidad 0.82 ya cae sola en el rango objetivo. Bajar solo
// la calidad, sin redimensionar, exigiría llegar a ~0.4 y se ve mal.
const MAX_DIMENSION = 1600;
const TARGET_BYTES = 400 * 1024;

// `toBlob` no acepta un peso objetivo, solo una calidad, así que hay que
// iterar: encodear, medir, bajar y repetir.
const INITIAL_QUALITY = 0.82;
const MIN_QUALITY = 0.5;
const QUALITY_STEP = 0.1;

export const scaleToFit = (width, height, maxDimension) => {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) {
    return { width, height };
  }
  const ratio = maxDimension / longest;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

const toBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob === null ? reject(new Error('No se pudo codificar la imagen')) : resolve(blob)),
      'image/jpeg',
      quality,
    );
  });

const fromObjectUrl = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('No se pudo leer la imagen'));
    };
    image.src = url;
  });

// `imageOrientation: 'from-image'` aplica la rotación del EXIF, que es lo que
// evita que una foto tomada con celular salga acostada. Safari no soporta el
// diccionario de opciones en todas sus versiones, de ahí el fallback por <img>.
const decode = async file => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (error) {
      // Cae al fallback de abajo.
    }
  }
  return fromObjectUrl(file);
};

const withJpgExtension = name =>
  `${name.replace(/\.[^.]+$/, '') || 'foto'}.jpg`;

export const compressImage = async file => {
  // Una imagen que ya es chica y de dimensiones razonables se sube tal cual:
  // reencodearla a JPEG puede dejarla más pesada, y además así un PNG con
  // transparencia conserva su canal alfa.
  const source = await decode(file);
  const sourceWidth = source.width;
  const sourceHeight = source.height;

  if (file.size <= TARGET_BYTES && Math.max(sourceWidth, sourceHeight) <= MAX_DIMENSION) {
    if (typeof source.close === 'function') source.close();
    return file;
  }

  const { width, height } = scaleToFit(sourceWidth, sourceHeight, MAX_DIMENSION);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(source, 0, 0, width, height);
  if (typeof source.close === 'function') source.close();

  let quality = INITIAL_QUALITY;
  let blob = await toBlob(canvas, quality);
  while (blob.size > TARGET_BYTES && quality > MIN_QUALITY) {
    quality = Math.max(MIN_QUALITY, quality - QUALITY_STEP);
    blob = await toBlob(canvas, quality);
  }

  return new File([blob], withJpgExtension(file.name), { type: 'image/jpeg' });
};
